/**
 * Where a filter selection lives under the catalog grammar (SPEC-0016, SPEC-0018 §5.2).
 *
 * The JavaScript twin of IDX\Services\Url\FilterPageUrl, for the site popup and the
 * home-page filter; FilterPageUrlParityTest runs one table of cases through both.
 *
 *  - A type makes it a path: `/{city}[/{district}]/{type}[/{child}]` or, with no city,
 *    `/real-estate/{type}`. A city without a type is the city page; nothing at all is
 *    `/real-estate`.
 *  - Under a type the selection NAMES the segments whose GET equivalent it carries,
 *    among the enabled ones applied to the type, in priority order. The page's own
 *    segment keeps the path while still selected; otherwise the first named one takes
 *    it. A rental selection names none — filter pages are sale only.
 *  - Everything else stays in the query string.
 *
 * `options` is the FilterOptions payload, `context` the page the visitor is on
 * (window.idxFilterContext, or null), `form` the whole selection. `named`, when given,
 * is the filter page /idx/count chose for the selection (a slug, or null for none): it
 * replaces the local choice, which cannot tell a page that answers from one that 404s.
 */
const FLAGS = ['waterfront', 'pool'];

function present(value) {
    return value !== null && value !== undefined && value !== '' && value !== false;
}

function normalise(key, value) {
    if (FLAGS.includes(key)) return value === true || value === 1 || value === '1' || value === 'true';
    if (key === 'sort') return String(value);
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? null : n;
}

export function buildCatalogUrl(options, context, form, named) {
    const city = form.city || null;
    const district = city ? form.district || null : null;
    const type = form.type || null;

    const sameBase = !!context
        && (context.city || null) === city
        && (context.district || null) === district
        && (context.type || null) === type;
    const child = type && city && sameBase ? context.child || null : null;
    // A category (SPEC-0031) keeps the popup on itself, with or without a city. It
    // has no filter pages under it, so a selection only travels in the query.
    const category = type && sameBase ? context.category || null : null;
    // The page's own segment keeps the path while still selected — here, and on the
    // server, which the popup sends it to.
    const current = sameBase ? context.segment || null : null;

    const values = { ...form };
    ['city', 'district', 'type', 'child'].forEach((key) => delete values[key]);

    let segment = null;

    if (type && !category && !['rent', 'lease'].includes(values.offer)) {
        if (named !== undefined) {
            segment = (options.segments || []).some((s) => s.slug === named) ? named : null;
        } else {
            const denied = options.type_denied || {};
            const selected = (options.segments || [])
                .filter((s) => !s.facet || !(denied[s.facet] || []).includes(type))
                .filter((s) => !s.types || s.types.includes(type))
                .filter((s) => {
                    const wanted = Object.entries(s.query || {});
                    return wanted.length > 0 && wanted.every(([key, value]) => present(values[key]) && normalise(key, values[key]) === normalise(key, value));
                })
                .map((s) => s.slug);

            segment = selected.includes(current) ? current : selected[0] || null;
        }
    }

    if (segment) {
        const definition = options.segments.find((s) => s.slug === segment);
        Object.keys(definition.query || {}).forEach((key) => delete values[key]);
        if (values.offer === 'sale') delete values.offer;
    }

    const params = new URLSearchParams();
    Object.keys(values).forEach((key) => {
        const value = values[key];
        if (!present(value)) return;
        params.set(key, value === true ? '1' : String(value));
    });

    const cityUrl = (parts) => options.city_url.replace('__PATH__', parts.filter(Boolean).join('/'));

    let path;
    if (category) path = city ? cityUrl([city, district, type, category]) : [options.url, type, category].join('/');
    else if (type && city) path = cityUrl([city, district, type, child, segment]);
    else if (type) path = [options.url, type, segment].filter(Boolean).join('/');
    else if (city) path = cityUrl([city, district]);
    else path = options.url;

    const query = params.toString();

    return { url: path + (query ? '?' + query : ''), child, category, current };
}
